import DualDateRangePicker from 'components/Common/Calendar/DualDateRangePicker'
import VerticalDateRangePicker from 'components/Common/Calendar/VerticalDateRangePicker'
import { type CalendarFocusMonth, type CalendarVisibleMonths, clampCalendarFocusMonth } from 'components/Common/Calendar/calendarFocusMonth'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import AISparkleIcon from 'components/Search/SearchForm/AISparkleIcon'
import type { DurationOption } from 'components/Search/SearchForm/SearchDateInput/FlexibleDatePicker/DurationPicker'
import Group from 'components/utils/Group'
import { NATURAL_LANGUAGE_SEARCH_AI_GLOW_EFFECT_TIME_OUT_MS } from 'constants/naturalLanguageSearch'
import type { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { DATE_SEARCH_OPTION_IDS, DEFAULT_FLEXIBLE_DURATION_RANGE } from 'constants/search'
import type { SearchCalendarEvent } from 'constants/searchCalendarEvents'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { mapFlexibleDatesToTypeaheadFormat } from 'lib/search/searchUtils'
import {
  maybeTrackSearchCalendarEventOverlayApply,
  trackSearchCalendarEventOverlayHintHover,
} from 'analytics/snowplow/events/searchCalendarEventOverlayEvents'
import {
  GlobalSearchDispatchContext,
  GlobalSearchStateContext,
} from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { addDays, addMonths, beginningOfToday } from 'lib/datetime/dateUtils'
import type moment from 'moment'
import type { ComponentProps } from 'react'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled, { css, keyframes } from 'styled-components'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import FlexibleDatePicker from './FlexibleDatePicker'
import FormattedSearchDate from './FormattedSearchDate'
import SearchCalendarEventLegend from './SearchCalendarEventLegend'
import SearchDateToggle from './SearchDateToggle'
import useSearchCalendarEventHoverTracking from 'hooks/Search/useSearchCalendarEventHoverTracking'
import useSearchCalendarEventNames from 'hooks/Search/useSearchCalendarEventNames'
import useSearchCalendarEventOverlay from 'hooks/Search/useSearchCalendarEventOverlay'
import { useScreenSizeOnly } from 'hooks/useScreenSize'

const confirmationGlowPulse = keyframes`
  0%, 100% {
    box-shadow:
      0 0 0 ${rem(2)} var(--date-confirmation-glow-border),
      0 0 ${rem(6)} var(--date-confirmation-glow-soft),
      0 0 ${rem(14)} var(--date-confirmation-glow-strong);
  }
  50% {
    box-shadow:
      0 0 0 ${rem(2)} var(--date-confirmation-glow-border),
      0 0 ${rem(12)} var(--date-confirmation-glow-soft),
      0 0 ${rem(24)} var(--date-confirmation-glow-strong);
  }
`

const DateConfirmationShell = styled.div<{ $glowing: boolean }>`
  width: fit-content;
  max-width: 100%;
  margin-inline: auto;
  border-radius: ${props => props.theme.borderRadius.S};
  --date-confirmation-glow-border: ${props => props.theme.palette.product.ai.dark};
  --date-confirmation-glow-soft: color-mix(
    in srgb,
    ${props => props.theme.palette.product.ai.dark} 55%,
    transparent
  );
  --date-confirmation-glow-strong: color-mix(
    in srgb,
    ${props => props.theme.palette.product.ai.dark} 35%,
    transparent
  );
  transition: box-shadow 300ms ease;

  ${props => props.$glowing && css`
    animation: ${confirmationGlowPulse} 1.5s ease-in-out infinite;
  `}
`

const DateConfirmationBanner = styled(MessageBanner)`
  width: fit-content;
  max-width: 100%;
  border-radius: ${props => props.theme.borderRadius.S};
`

/** Delegation target for event-overlay hover tracking; purely a wrapper. */
const CalendarHoverTarget = styled.div`
  display: contents;
`

interface Props extends Pick<ComponentProps<typeof DropdownSheet>, 'anchorRef' | 'triggerRef' | 'open'> {
  /**
   * `shouldPerformSearch` runs the search immediately. It must be passed explicitly:
   * wiring this straight to `onClick` only worked because React handed it a MouseEvent,
   * which is truthy by accident rather than by intent.
   */
  onDatesApply: (shouldPerformSearch?: boolean) => void
  onDatesDecline: () => void
  anytimeDateSelected?: boolean
  selectAnytimeDate?: (shouldApply: boolean) => void
  selectSpecificDates?: () => void
  selectFlexibleDates?: () => void
  selectAnytime?: () => void
  dateSearchOptionId?: DATE_SEARCH_OPTION_IDS
  durationOptionsCustom?: Array<DurationOption>
  isSpecificSearchEnabled?: boolean
  showFlexibleYearSelector?: boolean
  closeMenu?: () => void
  disableFlexible?: boolean
  /** Called when user manually selects dates in the calendar. Omit dates for check-in-only interaction. */
  onUserDatesChanged?: (dates?: App.TypeaheadExtractedTravelDates) => void
  /**
   * Month the calendar should open on when no check-in date has been picked yet.
   * Only moves the viewport — it never selects a date.
   */
  focusMonth?: CalendarFocusMonth
  showDateConfirmationPrompt?: boolean
}

function SearchDateDropDown(props: Props) {
  const {
    anchorRef,
    triggerRef,
    open,
    onDatesApply,
    onDatesDecline,
    anytimeDateSelected,
    selectAnytimeDate,
    selectSpecificDates,
    selectFlexibleDates,
    dateSearchOptionId,
    durationOptionsCustom,
    isSpecificSearchEnabled = true,
    showFlexibleYearSelector,
    onUserDatesChanged,
    closeMenu,
    disableFlexible,
    focusMonth,
    showDateConfirmationPrompt = false,
  } = props
  const { checkinDate, checkoutDate, flexibleMonths, flexibleNights, userSelectedFlexibleMonths } = useContext(GlobalSearchStateContext)
  const globalSearchDispatch = useContext(GlobalSearchDispatchContext)
  const intl = useIntl()
  const isMobileScreen = useScreenSizeOnly('mobile')
  const today = useMemo(() => beginningOfToday(), [])
  const [isConfirmationGlowing, setIsConfirmationGlowing] = useState(false)
  const [visibleMonths, setVisibleMonths] = useState<CalendarVisibleMonths>()

  const localInputRef = useRef<HTMLInputElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) setVisibleMonths(undefined)
  }, [open])

  const calendarEventOverlay = useSearchCalendarEventOverlay(open ? visibleMonths : undefined, open)
  const calendarEventHoverHandlers = useSearchCalendarEventHoverTracking(
    calendarEventOverlay.placeIds,
    calendarEventOverlay.showOverlay,
  )
  const adjacentEvents = useMemo(
    () => [calendarEventOverlay.previousMonthEvent, calendarEventOverlay.nextMonthEvent]
      .filter((event): event is SearchCalendarEvent => !!event),
    [calendarEventOverlay.nextMonthEvent, calendarEventOverlay.previousMonthEvent],
  )
  const adjacentEventNames = useSearchCalendarEventNames(adjacentEvents)

  const onHintHover = useCallback((calendarEventId: string) => {
    trackSearchCalendarEventOverlayHintHover({ calendarEventId, placeIds: calendarEventOverlay.placeIds })
  }, [calendarEventOverlay.placeIds])

  const buildMonthPreview = useCallback((event: SearchCalendarEvent | undefined) => {
    if (!event || !calendarEventOverlay.isAnyVariant) return undefined

    // Same copy as the footer legend chip, so a hint and the range it points at read alike.
    const label = adjacentEventNames[event.id]?.label
    if (!label) return undefined

    return { event, label, onHover: onHintHover }
  }, [adjacentEventNames, calendarEventOverlay.isAnyVariant, onHintHover])

  const previousMonthPreview = useMemo(
    () => buildMonthPreview(calendarEventOverlay.previousMonthEvent),
    [buildMonthPreview, calendarEventOverlay.previousMonthEvent],
  )
  const nextMonthPreview = useMemo(
    () => buildMonthPreview(calendarEventOverlay.nextMonthEvent),
    [buildMonthPreview, calendarEventOverlay.nextMonthEvent],
  )

  const overlayEvents = useMemo(() => {
    if (!calendarEventOverlay.showOverlay || !calendarEventOverlay.primaryEvents.length) return undefined
    return calendarEventOverlay.primaryEvents
  }, [calendarEventOverlay.primaryEvents, calendarEventOverlay.showOverlay])
  const overlayHolidays = useMemo(() => {
    if (!calendarEventOverlay.showOverlay || !calendarEventOverlay.publicHolidays.length) return undefined
    return calendarEventOverlay.publicHolidays
  }, [calendarEventOverlay.publicHolidays, calendarEventOverlay.showOverlay])

  useEffect(() => {
    if (!showDateConfirmationPrompt) {
      setIsConfirmationGlowing(false)
      return
    }

    setIsConfirmationGlowing(true)
    const timer = setTimeout(() => {
      setIsConfirmationGlowing(false)
    }, NATURAL_LANGUAGE_SEARCH_AI_GLOW_EFFECT_TIME_OUT_MS)

    return () => clearTimeout(timer)
  }, [showDateConfirmationPrompt])

  // show flexible search if the search option is flexible dates or if the search option is anytime with no specific search enabled
  const shouldShowFlexibleSearch = dateSearchOptionId === DATE_SEARCH_OPTION_IDS.FLEXIBLE ||
    (!isSpecificSearchEnabled && dateSearchOptionId === DATE_SEARCH_OPTION_IDS.ANYTIME)

  const shouldShowSpecific = dateSearchOptionId === DATE_SEARCH_OPTION_IDS.SPECIFIC ||
    (isSpecificSearchEnabled && dateSearchOptionId === DATE_SEARCH_OPTION_IDS.ANYTIME)

  useEffect(() => {
    if (localInputRef?.current) {
      localInputRef.current.focus()
    }
  }, [localInputRef])

  const maxDate = useMemo(
    () =>
      checkinDate && !checkoutDate ?
          addDays(checkinDate.toDate(), 31) :
          addMonths(today, 36),
    [checkinDate, checkoutDate, today],
  )

  // An existing check-in date already drives the calendar, so the hint only
  // applies before the traveller has picked anything.
  const calendarFocusDate = useMemo(
    () => checkinDate ? undefined : clampCalendarFocusMonth(focusMonth, today, maxDate),
    [checkinDate, focusMonth, maxDate, today],
  )

  const handleDatesChanged = useCallback(
    // changed types here to match the DualDateRangePicker props
    (dates: { startDate: moment.Moment, endDate: moment.Moment | undefined }) => {
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_CHECKIN_DATE,
        date: dates.startDate,
      })
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_CHECKOUT_DATE,
        date: dates.endDate,
      })

      // Notify on any calendar interaction so extracted dates stop overriding edits,
      // including while the confirmation banner is showing. Only commit a full range
      // once checkout is chosen — never fabricate checkOut = checkIn.
      if (dates.startDate && dates.endDate) {
        onUserDatesChanged?.({
          type: 'specific',
          checkIn: dates.startDate.format(ISO_DATE_FORMAT),
          checkOut: dates.endDate.format(ISO_DATE_FORMAT),
        })
      } else if (dates.startDate) {
        onUserDatesChanged?.()
      }

      if (dates.startDate && dates.endDate) {
        selectSpecificDates?.()
      }
    },
    [globalSearchDispatch, selectSpecificDates, onUserDatesChanged],
  )

  const handleFlexibleNightsChanged = useCallback(
    (newFlexibleNights: FLEXIBLE_DURATION_RANGE) => {
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_FLEXIBLE_DURATION,
        flexibleNights: newFlexibleNights,
      })
      if (newFlexibleNights && flexibleMonths) {
        selectFlexibleDates?.()
        onUserDatesChanged?.(mapFlexibleDatesToTypeaheadFormat(flexibleMonths, newFlexibleNights))
      }
    },
    [globalSearchDispatch, selectFlexibleDates, onUserDatesChanged, flexibleMonths],
  )

  const handleFlexibleMonthOptionsChanged = useCallback(
    (flexibleMonthOptions: string) => {
      globalSearchDispatch({
        type: GlobalSearchStateActions.SET_FLEXIBLE_MONTH_RANGE,
        flexibleMonths: flexibleMonthOptions,
      })
      if (flexibleMonthOptions.length > 0) {
        selectFlexibleDates?.()
        globalSearchDispatch({
          type: GlobalSearchStateActions.SET_USER_SELECTED_FLEXIBLE_MONTHS,
          userSelectedFlexibleMonths: true,
        })
        const currentFlexibleNights = flexibleNights || DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION
        onUserDatesChanged?.(mapFlexibleDatesToTypeaheadFormat(flexibleMonthOptions, currentFlexibleNights))
      } else {
        globalSearchDispatch({
          type: GlobalSearchStateActions.UNSET_USER_SELECTED_FLEXIBLE_MONTHS,
        })
      }
    },
    [globalSearchDispatch, selectFlexibleDates, onUserDatesChanged, flexibleNights],
  )

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      selectAnytimeDate?.(true)
    }
  }, [selectAnytimeDate])

  const selectAnytimeDateNoApply = useCallback(() => {
    selectAnytimeDate?.(false)
    onUserDatesChanged?.({ type: 'anytime' })
  }, [selectAnytimeDate, onUserDatesChanged])

  const onSelectSpecificDates = useCallback(() => {
    selectSpecificDates?.()
    // Switching date mode counts as user ownership of dates — dismiss soft-confirm.
    onUserDatesChanged?.()
  }, [selectSpecificDates, onUserDatesChanged])

  const onSelectFlexibleDates = useCallback(() => {
    selectFlexibleDates?.()
    // Switching date mode counts as user ownership of dates — dismiss soft-confirm.
    onUserDatesChanged?.()
  }, [selectFlexibleDates, onUserDatesChanged])

  const hasDates = !!(checkinDate && checkoutDate)
  const disableSearchButton = dateSearchOptionId === DATE_SEARCH_OPTION_IDS.SPECIFIC && !hasDates

  const handleDatesApply = useCallback(() => {
    maybeTrackSearchCalendarEventOverlayApply({
      isTurnedOn: calendarEventOverlay.isTurnedOn,
      events: calendarEventOverlay.matchingEvents,
      hadVisibleSuggestion: calendarEventOverlay.hadVisibleSuggestion,
      hadHintedSuggestion: calendarEventOverlay.hadHintedSuggestion,
      placeIds: calendarEventOverlay.placeIds,
      checkInIso: checkinDate?.format(ISO_DATE_FORMAT),
      checkOutIso: checkoutDate?.format(ISO_DATE_FORMAT),
    })

    onDatesApply(true)
  }, [calendarEventOverlay, checkinDate, checkoutDate, onDatesApply])

  return <DropdownSheet
    data-testid="search-date-dropdown"
    size="fill-anchor"
    open={open}
    onClose={closeMenu}
    anchorRef={anchorRef}
    triggerRef={triggerRef}
    bodyRef={bodyRef}
    primaryActionProps={{
      'data-testid': 'search-apply-button',
      children: intl.formatMessage({
        id: 'dUn1eYBZXOHncdrqjYwc',
        defaultMessage: 'Apply',
        description: 'Button text to apply selected dates in search date picker',
      }),
      disabled: disableSearchButton,
      onClick: handleDatesApply,
    }}
    secondaryActionProps={{
      'data-testid': 'search-nodates-button',
      kind: 'tertiary',
      children: intl.formatMessage({
        id: 'sF9A_o8aEaK3HKqs6QYk',
        defaultMessage: 'Cancel',
        description: 'Button text to cancel date selection in search date picker',
      }),
      onClick: onDatesDecline,
    }}
    footerStart={<Group direction="horizontal" gap={12} verticalAlign="center" wrap="wrap" fullWidth>
      {!!(overlayEvents || overlayHolidays) && (
        <SearchCalendarEventLegend
          events={overlayEvents ?? []}
          publicHolidays={overlayHolidays}
          placeIds={calendarEventOverlay.placeIds}
        />
      )}
      <Group direction="horizontal" grow horizontalAlign="end" verticalAlign="center">
        <FormattedSearchDate />
      </Group>
    </Group>}
  >
    <VerticalSpacer gap={32} onKeyDown={onKeyDown}>
      <Group
        direction="horizontal"
        horizontalAlign="center"
      >
        <SearchDateToggle
          onSelectAnytime={selectAnytimeDateNoApply}
          onSelectSpecific={onSelectSpecificDates}
          anytimeDateSelected={!!anytimeDateSelected}
          onSelectFlexible={onSelectFlexibleDates}
          dateSearchOptionId={dateSearchOptionId}
          isSpecificSearchEnabled={isSpecificSearchEnabled}
          disableFlexible={disableFlexible}
        />
      </Group>
      {showDateConfirmationPrompt && <DateConfirmationShell $glowing={isConfirmationGlowing}>
        <DateConfirmationBanner
          kind="informational"
          density="compact"
          icon={<AISparkleIcon size="S" />}
        >
          {intl.formatMessage({
            id: 'YU69oc2ZrMyXFnjwIawk',
            defaultMessage: 'Dates updated from your search.',
            description: 'Single-line notice in the desktop hotel date picker when natural-language search updates the selected travel dates',
          })}
        </DateConfirmationBanner>
      </DateConfirmationShell>}
      <Group direction="vertical" horizontalAlign="center">
        {shouldShowFlexibleSearch && (
          <FlexibleDatePicker
            onMonthChange={handleFlexibleMonthOptionsChanged}
            onDurationChange={handleFlexibleNightsChanged}
            previouslySelectedDuration={flexibleNights}
            previouslySelectedMonths={userSelectedFlexibleMonths ? flexibleMonths : undefined}
            durationOptionsCustom={durationOptionsCustom}
            showYearSelector={showFlexibleYearSelector}
          />
        )}
        {shouldShowSpecific && isMobileScreen && (
          <VerticalDateRangePicker
            onDatesChange={handleDatesChanged}
            startDate={checkinDate}
            endDate={checkoutDate}
            min={today}
            max={maxDate}
            contentRef={bodyRef}
            focusMonth={calendarFocusDate}
          />
        )}
        {shouldShowSpecific && !isMobileScreen && (
          <CalendarHoverTarget
            onMouseOver={calendarEventHoverHandlers.onMouseOver}
            onMouseOut={calendarEventHoverHandlers.onMouseOut}
          >
            <DualDateRangePicker
              onDatesChange={handleDatesChanged}
              startDate={checkinDate}
              endDate={checkoutDate}
              minDate={today}
              maxDate={maxDate}
              focusMonth={calendarFocusDate}
              onVisibleMonths={open ? setVisibleMonths : undefined}
              overlayEvents={overlayEvents}
              overlayHolidays={overlayHolidays}
              previousMonthPreview={previousMonthPreview}
              nextMonthPreview={nextMonthPreview}
            />
          </CalendarHoverTarget>
        )}
      </Group>
    </VerticalSpacer>
  </DropdownSheet>
}

export default SearchDateDropDown
